import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useCategories } from '../../hooks/useCategories';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { Badge } from '../../components/ui/Badge';
import { Eye, EyeOff, Shield, Upload, FileText, X, Users, Search, Filter, Plus, Edit, Trash2, Download, Info, AlertCircle, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../components/ui/DataTable';
import {
  parseBulkUserFile,
  registerBulkUsers,
  formatBulkRegistrationSummary,
} from '../../utils/bulkUserRegistration';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { registrationService } from '../../api/registrationService';
import { userManagementService } from '../../api/userManagementService';
import { assignRole, removeRole } from '../../api/roleManagementService';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  PHONE_COUNTRY_OPTIONS,
  parsePhoneForForm,
  validateRegistrationPhoneInput,
  normalizeRegistrationPhoneForApi,
} from '../../utils/phoneUtils';
import DefaultPasswordRequirementsPopover from '../../components/auth/DefaultPasswordRequirementsPopover';
import {
  getDefaultPasswordValidationMessage,
  isDefaultPasswordValid,
} from '../../utils/passwordValidation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/** Normalize is_password_changed from API / list row shapes */
function resolvePasswordChangedFlag(user) {
  if (!user) return false;
  const raw =
    user.isPasswordChanged ??
    user.is_password_changed ??
    user.passwordChanged;
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return true;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return false;
  return false;
}

const Register = () => {
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  /** One-shot: readonly until first focus blocks Chrome/email autofill on this filter field */
  const [userListSearchUnlocked, setUserListSearchUnlocked] = useState(false);
  const [passwordFieldUnlocked, setPasswordFieldUnlocked] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedUsers, setParsedUsers] = useState([]);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const bulkFileInputRef = useRef(null);
  const fieldScrollRefs = useRef({});
  const phoneValidationTimerRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    defaultPassword: '',
    role: '',
    department: '',
    category: '',  // Category for agent role
    isActive: false,  // Account Status: false = Inactive, true = Active
    isPasswordChanged: false  // Password changed flag
  });
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const { data: categoriesRaw = [], isLoading: loadingCategories } = useCategories();
  const categories = useMemo(
    () =>
      categoriesRaw.filter((cat) => {
        const name = cat.categoryName || cat.name || '';
        return cat.isActive === true && name !== 'Select a category...';
      }),
    [categoriesRaw]
  );
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [phoneRegion, setPhoneRegion] = useState('KE');
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Debounced search term for API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadRoles();
    loadDepartments();
  }, []);

  // Reload users when any filter changes (including debounced search)
  useEffect(() => {
    loadRegisteredUsers();
  }, [currentPage, pageSize, statusFilter, roleFilter, debouncedSearchTerm]);

  /**
   * Load roles dynamically from backend
   */
  const loadRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await registrationService.getRoles();
      if (response?.data && Array.isArray(response.data)) {
        setRoles(response.data);
      } else {
        // Fallback to default roles if API fails
        setRoles([
          { value: 'admin', label: 'Admin', roleId: null, roleCode: 'admin' },
          { value: 'agent', label: 'Agent', roleId: null, roleCode: 'agent' },
          { value: 'team-lead', label: 'Team Lead', roleId: null, roleCode: 'team_lead' },
          { value: 'customer', label: 'Customer', roleId: null, roleCode: 'customer' }
        ]);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      // Fallback to default roles
      setRoles([
        { value: 'admin', label: 'Admin', roleId: null, roleCode: 'admin' },
        { value: 'agent', label: 'Agent', roleId: null, roleCode: 'agent' },
        { value: 'team-lead', label: 'Team Lead', roleId: null, roleCode: 'team_lead' },
        { value: 'customer', label: 'Customer', roleId: null, roleCode: 'customer' }
      ]);
      toast.error('Failed to load roles. Using default roles.');
    } finally {
      setLoadingRoles(false);
    }
  };

  /**
   * Load departments from backend (using no-auth endpoint)
   * Gets tenantId from sessionStorage if available
   */
  const loadDepartments = async () => {
    setLoadingDepartments(true);
    try {
      // Get tenantId from sessionStorage (set during login)
      const tenantId = sessionStorage.getItem('tenantId');
      //console.log('🔍 Loading departments with tenantId:', tenantId);

      const response = await registrationService.getDepartments(tenantId ? parseInt(tenantId, 10) : null);
      //console.log('📦 Raw departments response:', response);

      // Handle response format from registrationService.getDepartments()
      // registrationService.getDepartments() returns: { data: [...] } where data is the departments array
      // The departments are already extracted in registrationService, so response.data should be the array
      let departmentsList = [];

      if (response?.data) {
        if (Array.isArray(response.data)) {
          // Direct array from registrationService
          departmentsList = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          // Paginated response format (shouldn't happen if registrationService parsed correctly)
          departmentsList = response.data.content;
        } else if (response.data.data) {
          // Extra nested level (shouldn't happen if registrationService parsed correctly)
          if (Array.isArray(response.data.data)) {
            departmentsList = response.data.data;
          } else if (response.data.data.content && Array.isArray(response.data.data.content)) {
            departmentsList = response.data.data.content;
          }
        }
      } else if (Array.isArray(response)) {
        // Direct array (shouldn't happen, but handle it)
        departmentsList = response;
      }

      //console.log('📋 Extracted departments list:', departmentsList);

      if (departmentsList.length > 0) {
        // Log departments to verify they have valid IDs
        setDepartments(departmentsList);
      } else {
        setDepartments([]);
        console.warn('⚠️ No departments found in database or response format is incorrect');
        toast.error('No departments available. Please create departments first.');
      }
    } catch (error) {
      console.error('❌ Error loading departments:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load departments';
      toast.error(`Failed to load departments: ${errorMessage}`);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  /**
   * Helper function to get roleId from role code
   */
  const getRoleIdFromCode = (roleCode) => {
    if (!roleCode || roleCode === 'all') return null;
    const role = roles.find(r => r.value === roleCode || r.roleCode === roleCode);
    return role?.roleId || null;
  };

  /** Build API params for user list (reused by loadRegisteredUsers and export) */
  const buildListParams = (overrides = {}) => {
    const params = {
      page: overrides.page !== undefined ? overrides.page : currentPage,
      size: overrides.size !== undefined ? overrides.size : pageSize,
      isActive: statusFilter === 'all' ? null : statusFilter === 'active',
      roleId: getRoleIdFromCode(roleFilter),
      search: debouncedSearchTerm || null
    };
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined) delete params[key];
    });
    return params;
  };

  /** Parse getUsers response and return { content, totalElements } with mapped users */
  const parseUsersResponse = (response) => {
    let users = [];
    let total = 0;
    if (response?.data?.data?.content) {
      users = response.data.data.content || [];
      total = response.data.data.totalElements || 0;
    } else if (response?.data?.content) {
      users = response.data.content || [];
      total = response.data.totalElements || 0;
    } else if (Array.isArray(response?.data)) {
      users = response.data;
      total = users.length;
    } else if (response?.data?.data && Array.isArray(response.data.data)) {
      users = response.data.data;
      total = users.length;
    }
    const mapped = users.map(user => {
      let roleCode = user.roleCode || user.role || 'customer';
      if (roleCode) roleCode = roleCode.toLowerCase().replace(/_/g, '-');
      return {
        id: user.userId || user.id,
        name: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown',
        email: user.email || '',
        phone: user.phone || '',
        role: roleCode,
        roleName: user.roleName || roleCode,
        status: user.isActive ? 'active' : 'inactive',
        createdAt: user.createdAt || user.created_at || null
      };
    });
    return { content: mapped, totalElements: total };
  };

  /**
   * Load users dynamically from backend with pagination and filters
   * All filters are applied server-side for correct pagination
   */
  const loadRegisteredUsers = async () => {
    setLoadingUsers(true);
    try {
      const params = buildListParams();
      const response = await userManagementService.getUsers(params);

      // Handle response format: { data: { data: { content: [...], totalElements, totalPages, ... } } }
      let users = [];
      let total = 0;
      let pages = 0;

      if (response?.data) {
        if (response.data.data && response.data.data.content) {
          users = response.data.data.content || [];
          total = response.data.data.totalElements || 0;
          pages = response.data.data.totalPages || 0;
        } else if (response.data.content) {
          users = response.data.content || [];
          total = response.data.totalElements || 0;
          pages = response.data.totalPages || 0;
        } else if (Array.isArray(response.data)) {
          users = response.data;
          total = users.length;
          pages = 1;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          users = response.data.data;
          total = users.length;
          pages = 1;
        }
      } else if (Array.isArray(response)) {
        users = response;
        total = users.length;
        pages = 1;
      }

      const mappedUsers = users.map(user => {
        let roleCode = user.roleCode || user.role || 'customer';
        if (roleCode) roleCode = roleCode.toLowerCase().replace(/_/g, '-');
        return {
          id: user.userId || user.id,
          name: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown',
          email: user.email || '',
          phone: user.phone || '',
          role: roleCode,
          roleId: user.roleId ?? user.role_id ?? null,
          roleName: user.roleName || roleCode,
          status: user.isActive ? 'active' : 'inactive',
          isActive: user.isActive,
          createdAt: user.createdAt || user.created_at || new Date().toISOString(),
          departmentId: user.departmentId || user.department_id,
          teamId: user.teamId || user.team_id || null,
          teamName: user.teamName || user.team_name || null,
          categoryId: user.categoryId || user.category_id,
          isPasswordChanged: resolvePasswordChangedFlag(user),
          profile: { phone: user.phone || '' }
        };
      });

      setRegisteredUsers(mappedUsers);
      setTotalUsers(total);
      setTotalPages(pages);
    } catch (error) {
      console.error('Error loading registered users:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load users';
      toast.error(errorMessage);
      setRegisteredUsers([]);
      setTotalUsers(0);
      setTotalPages(0);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Reset department when role changes away from agent/team-lead
      if (field === 'role' && value !== 'agent' && value !== 'team-lead') {
        newData.department = '';
      }

      // Reset category when role changes away from agent
      if (field === 'role' && value !== 'agent') {
        newData.category = '';
      }

      return newData;
    });
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    // Reset email availability status when email changes
    if (field === 'email') {
      setEmailAvailable(null);
    }
    if (field === 'phone') {
      setPhoneAvailable(null);
      schedulePhoneValidation(value);
    }
  };

  useEffect(() => {
    return () => {
      if (phoneValidationTimerRef.current) {
        clearTimeout(phoneValidationTimerRef.current);
      }
    };
  }, []);

  const runPhoneValidation = async (phone) => {
    const trimmed = (phone || '').trim();
    if (!trimmed) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: '' }));
      return;
    }

    const phoneValidation = validateRegistrationPhoneInput(trimmed);
    if (!phoneValidation.valid) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: phoneValidation.message }));
      return;
    }

    const storedPhone = (editingUser?.phone || editingUser?.phoneE164 || '').trim();
    if (editingUser && storedPhone && trimmed === storedPhone) {
      setPhoneAvailable(true);
      setErrors((prev) => ({ ...prev, phone: '' }));
      return;
    }

    setPhoneChecking(true);
    try {
      const response = await registrationService.checkPhoneAvailability(
        trimmed,
        null,
        null,
        editingUser?.id || editingUser?.userId || null
      );
      if (response?.available === true) {
        setPhoneAvailable(true);
        setErrors((prev) => ({ ...prev, phone: '' }));
      } else if (response?.available === false) {
        setPhoneAvailable(false);
        setErrors((prev) => ({
          ...prev,
          phone: response?.message || 'Phone number is already registered',
        }));
      } else {
        setPhoneAvailable(null);
        setErrors((prev) => ({
          ...prev,
          phone: response?.message || 'Could not verify phone number',
        }));
      }
    } catch (error) {
      console.error('Phone check error:', error);
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: 'Failed to check phone availability' }));
    } finally {
      setPhoneChecking(false);
    }
  };

  const schedulePhoneValidation = (phone) => {
    if (phoneValidationTimerRef.current) {
      clearTimeout(phoneValidationTimerRef.current);
    }
    const trimmed = (phone || '').trim();
    if (!trimmed) {
      setPhoneAvailable(null);
      setErrors((prev) => ({ ...prev, phone: '' }));
      return;
    }
    phoneValidationTimerRef.current = setTimeout(() => {
      runPhoneValidation(trimmed);
    }, 500);
  };

  const handlePhoneBlur = (phone) => {
    if (phoneValidationTimerRef.current) {
      clearTimeout(phoneValidationTimerRef.current);
      phoneValidationTimerRef.current = null;
    }
    runPhoneValidation(phone);
  };

  const checkEmailAvailability = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailAvailable(null);
      return;
    }

    // Skip check if editing existing user with same email
    if (editingUser && editingUser.email === email) {
      setEmailAvailable(true);
      return;
    }

    setEmailChecking(true);
    try {
      // tenantId removed - backend will extract from JWT token
      const response = await registrationService.checkEmailAvailability(email);

      // Backend returns: { available: true/false, message: "..." }
      // registrationService now returns unwrapped response
      if (response?.available === true) {
        setEmailAvailable(true);
        setErrors(prev => ({ ...prev, email: '' }));
      } else if (response?.available === false) {
        setEmailAvailable(false);
        setErrors(prev => ({ ...prev, email: 'Email is already registered' }));
      } else {
        // Response structure unclear or error
        setEmailAvailable(null);
        console.warn('Unexpected email check response format:', response);
        setErrors(prev => ({ ...prev, email: 'Failed to check email availability' }));
      }
    } catch (error) {
      console.error('Email check error:', error);
      setEmailAvailable(null);
      setErrors(prev => ({ ...prev, email: 'Failed to check email availability' }));
    } finally {
      setEmailChecking(false);
    }
  };

  const computeValidationErrors = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (emailAvailable === false) {
      newErrors.email = 'Email is already registered';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneValidation = validateRegistrationPhoneInput(formData.phone);
      if (!phoneValidation.valid) {
        newErrors.phone = phoneValidation.message;
      } else if (phoneAvailable === false) {
        newErrors.phone = 'Phone number is already registered';
      }
    }

    // Password is only required when creating a new user, not when editing
    if (!editingUser) {
      const passwordMessage = getDefaultPasswordValidationMessage(formData.defaultPassword);
      if (passwordMessage) {
        newErrors.defaultPassword = passwordMessage;
      }
    } else if (formData.defaultPassword.trim()) {
      const passwordMessage = getDefaultPasswordValidationMessage(formData.defaultPassword);
      if (passwordMessage) {
        newErrors.defaultPassword = passwordMessage;
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Department is optional for all roles (including agent/team-lead)
    // No validation needed

    return newErrors;
  };

  const getFirstErrorScrollKey = (newErrors) => {
    const orderedKeys = ['name', 'email', 'phone', 'defaultPassword', 'role'];
    for (const k of orderedKeys) {
      if (newErrors[k]) return k;
    }
    return Object.keys(newErrors)[0] ?? null;
  };

  const scrollToFirstErrorField = (key) => {
    if (!key) return;
    const el = fieldScrollRefs.current[key];
    if (!el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable =
      el.querySelector('input:not([type="hidden"]):not([type="file"]), textarea, select, [role="combobox"]');
    if (focusable && typeof focusable.focus === 'function') {
      window.setTimeout(() => {
        try {
          focusable.focus({ preventScroll: true });
        } catch {
          focusable.focus();
        }
      }, 400);
    }
  };

  const createUserPasswordBlocked = editingUser
    ? Boolean(formData.defaultPassword.trim() && !isDefaultPasswordValid(formData.defaultPassword))
    : !isDefaultPasswordValid(formData.defaultPassword);
  const defaultPasswordInputInvalid =
    Boolean(errors.defaultPassword) ||
    (formData.defaultPassword && createUserPasswordBlocked);

  const handleCreateUser = async () => {
    const newErrors = computeValidationErrors();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = getFirstErrorScrollKey(newErrors);
      window.setTimeout(() => scrollToFirstErrorField(firstKey), 0);
      return;
    }

    setLoading(true);

    try {
      // Split name into first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Find the selected role object to get roleId
      const selectedRole = roles.find(r => r.value === formData.role);
      if (!selectedRole || !selectedRole.roleId) {
        toast.error('Invalid role selected. Please select a valid role.');
        setLoading(false);
        return;
      }

      const phone = normalizeRegistrationPhoneForApi(formData.phone);

      const userData = {
        email: formData.email.trim().toLowerCase(),
        username: formData.email.trim().toLowerCase().split('@')[0], // Use email prefix as username
        fullName: formData.name.trim(),
        firstName: firstName,
        lastName: lastName,
        phone,
        roleId: selectedRole.roleId, // Send roleId instead of role code
        // role: formData.role.toUpperCase().replace('-', '_'), // DEPRECATED: Kept for backward compatibility
        // tenantId removed - will be extracted from JWT token in backend (admin's tenant)
        defaultPassword: formData.defaultPassword.trim(),
      };

      // Include department if provided (optional for all roles)
      if (formData.department && formData.department.trim() !== '') {
        const deptId = parseInt(formData.department.trim(), 10);
        if (!isNaN(deptId) && deptId > 0) {
          // Ensure departmentId is a number (not string) for backend
          userData.departmentId = Number(deptId);
          //console.log('✓ Setting departmentId:', userData.departmentId, '(type:', typeof userData.departmentId, ')', 'for role:', formData.role);
        } else {
          console.error('✗ Invalid department ID:', formData.department, 'parsed as:', deptId);
          toast.error('Invalid department selected. Please select a valid department.');
          setLoading(false);
          return;
        }
      }
      // Department is optional - if not provided, userData.departmentId will be undefined/null

      // Include categoryId if role is agent and category is selected (optional)
      if (formData.role === 'agent' && formData.category && formData.category.trim() !== '') {
        const catId = parseInt(formData.category.trim(), 10);
        if (!isNaN(catId) && catId > 0) {
          userData.categoryId = Number(catId);
          //console.log('✓ Setting categoryId:', userData.categoryId, 'for role:', formData.role);
        }
      }

      // Log the userData being sent for debugging
      //console.log('📤 User data being sent to backend:', JSON.stringify(userData, null, 2));
      //console.log('📤 DepartmentId in userData:', userData.departmentId, '(type:', typeof userData.departmentId, ')');

      let response;
      if (editingUser) {
        // Update existing user via backend API. Role changes go through assignRole/removeRole below.
        const updatePhone = normalizeRegistrationPhoneForApi(formData.phone);

        const updateData = {
          fullName: formData.name.trim(),
          firstName: firstName,
          lastName: lastName,
          phone: updatePhone,
          departmentId: formData.department ? parseInt(formData.department) : null,
          categoryId: formData.category ? parseInt(formData.category) : null,
          isActive: formData.isActive
          // Role is changed via the existing assign/remove role APIs after this update.
          // is_password_changed is always set true on the server after admin update
        };

        // Only include password if provided
        if (formData.defaultPassword.trim()) {
          updateData.password = formData.defaultPassword.trim();
        }

        //console.log('📤 Updating user with data:', JSON.stringify(updateData, null, 2));
        response = await userManagementService.updateUser(editingUser.id, updateData);

        if (response?.data) {
          const previousRoleId = Number(editingUser.roleId ?? getRoleIdFromCode(editingUser.role));
          const nextRoleId = Number(selectedRole.roleId);
          if (nextRoleId && previousRoleId !== nextRoleId) {
            // Assign first so the user is never left with zero roles (remove rejects the last role).
            try {
              await assignRole(editingUser.id, nextRoleId);
            } catch (assignError) {
              const alreadyAssigned = (extractErrorMessage(assignError) || '')
                .toLowerCase()
                .includes('already assigned');
              if (!alreadyAssigned) throw assignError;
            }
            if (previousRoleId) {
              await removeRole(editingUser.id, previousRoleId);
            }
          }
          toast.success('User updated successfully!');
          setFormData((prev) => ({
            ...prev,
            isPasswordChanged: resolvePasswordChangedFlag(response.data) || true,
          }));
          await loadRegisteredUsers();
        } else {
          toast.error('Failed to update user. Please try again.');
        }
      } else {
        // Create new user via backend API
        //console.log('🚀 Calling registerByAdmin with userData:', userData);
        response = await registrationService.registerByAdmin(userData);

        //console.log('📥 Backend response:', response);
        //console.log('📥 Response data:', response?.data);

        if (response?.data) {
          // Log the created user's departmentId from response
          const createdUser = response.data;
          //console.log('✅ User created successfully!');
          //console.log('📊 Created user departmentId:', createdUser.departmentId || createdUser.data?.departmentId);
          toast.success('User registered successfully!');
          // Reload users to get the new user
          await loadRegisteredUsers();
        } else {
          console.error('❌ Failed to register user. Response:', response);
          toast.error('Failed to register user. Please try again.');
        }
      }

      if (response?.data || editingUser) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          defaultPassword: '',
          role: '',
          department: '',
          category: '',  // Reset category field
          isActive: false,
          isPasswordChanged: false
        });
        setPhoneRegion('KE');
        setPhoneAvailable(null);
        setErrors({});
        setEditingUser(null);
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      console.error('Error registering user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to register user. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    //console.log("user ==============> ", user);
    const rawPhone = (user.phone || user.phoneE164 || user.profile?.phone || '').trim();
    const parsedPhone = parsePhoneForForm(rawPhone, 'KE');
    setPhoneRegion(parsedPhone.region);

    const passwordChanged = resolvePasswordChangedFlag(user);

    // Determine isActive value - check multiple possible field names
    const isActive = user.isActive !== undefined ? user.isActive :
      (user.status === 'active' ? true : false);

    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: rawPhone,
      defaultPassword: '', // Don't pre-fill password for security
      role: user.role || '',
      department: user.departmentId ? user.departmentId.toString() : '',
      category: user.categoryId ? user.categoryId.toString() : '',
      isActive: isActive,  // Load current status
      isPasswordChanged: passwordChanged  // Load current password changed status
    });

    setIsCreateModalOpen(true);
  };

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      const response = await userManagementService.deleteUser(userToDelete);

      if (response?.data || response) {
        toast.success('User deleted successfully!');
        await loadRegisteredUsers();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while deleting the user';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const escapeCsvCell = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val).trim();
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const formatDateForExport = (dateVal) => {
    if (dateVal == null || dateVal === '') return '';
    try {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
    } catch { return ''; }
  };

  const fetchAllUsersForExport = async () => {
    const initialParams = buildListParams({ page: 0, size: 10 });
    const initialResponse = await userManagementService.getUsers(initialParams);
    const { totalElements } = parseUsersResponse(initialResponse);
    if (totalElements === 0) return [];
    const fullParams = buildListParams({ page: 0, size: totalElements });
    const fullResponse = await userManagementService.getUsers(fullParams);
    const { content } = parseUsersResponse(fullResponse);
    return content;
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const list = await fetchAllUsersForExport();
      if (list.length === 0) {
        toast('No users to export.', { icon: 'ℹ️' });
        return;
      }
      const headers = ['User Name', 'Email', 'Phone', 'Role', 'Status', 'Registered'];
      const rows = list.map(u => [
        escapeCsvCell(u.name || ''),
        escapeCsvCell(u.email || ''),
        escapeCsvCell(u.phone || ''),
        escapeCsvCell((u.roleName || u.role || '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')),
        escapeCsvCell(u.status || ''),
        escapeCsvCell(formatDateForExport(u.createdAt))
      ]);
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${list.length} user(s) to CSV.`);
    } catch (error) {
      console.error('Export CSV failed:', error);
      toast.error(extractErrorMessage(error, 'Failed to export users. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const list = await fetchAllUsersForExport();
      if (list.length === 0) {
        toast('No users to export.', { icon: 'ℹ️' });
        return;
      }
      const doc = new jsPDF('l', 'mm', 'a4');
      const headers = [['User Name', 'Email', 'Phone', 'Role', 'Status', 'Registered']];
      const body = list.map(u => [
        (u.name || '').substring(0, 30),
        (u.email || '').substring(0, 35),
        (u.phone || '').substring(0, 18),
        ((u.roleName || u.role || '').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '').substring(0, 12),
        (u.status || '').substring(0, 10),
        formatDateForExport(u.createdAt)
      ]);
      const config = {
        head: headers,
        body,
        startY: 20,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [34, 197, 94] }
      };
      if (typeof autoTable === 'function') {
        autoTable(doc, config);
      } else if (typeof doc.autoTable === 'function') {
        doc.autoTable(config);
      } else {
        throw new Error('PDF table plugin not available.');
      }
      doc.save(`users-export-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Exported ${list.length} user(s) to PDF.`);
    } catch (error) {
      console.error('Export PDF failed:', error);
      toast.error(extractErrorMessage(error, 'Failed to export users to PDF. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  const resetBulkUploadState = () => {
    setSelectedFile(null);
    setParsedUsers([]);
    setBulkUploadResult(null);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel (.xlsx, .xls) file.');
      return;
    }

    setSelectedFile(file);
    setUploadingFile(true);
    setBulkUploadResult(null);

    try {
      const users = await parseBulkUserFile(file);

      if (users.length === 0) {
        toast.error('No valid user data found. Ensure name and email columns are present.');
        setSelectedFile(null);
        return;
      }

      setParsedUsers(users);
      toast.success(`File processed. Found ${users.length} user(s) ready to register.`);
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(extractErrorMessage(error, 'Failed to process the uploaded file.'));
      setSelectedFile(null);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleBulkCreateUsers = async () => {
    if (parsedUsers.length === 0) return;

    if (!roles.some((r) => r.roleId)) {
      toast.error('Roles are not loaded. Please refresh the page and try again.');
      return;
    }

    setLoading(true);
    setBulkUploadResult(null);

    try {
      const result = await registerBulkUsers(parsedUsers, roles, {
        checkEmail: registrationService.checkEmailAvailability,
        checkPhone: registrationService.checkPhoneAvailability,
        registerUser: registrationService.registerByAdmin,
      });

      setBulkUploadResult(result);

      if (result.created.length > 0) {
        await loadRegisteredUsers();
      }

      const summary = formatBulkRegistrationSummary(result);
      if (result.created.length > 0 && result.alreadyExists.length === 0 && result.failed.length === 0) {
        toast.success(`Bulk upload successful. ${summary}.`);
        resetBulkUploadState();
        setIsBulkUploadModalOpen(false);
      } else if (result.created.length > 0) {
        toast.success(`Partially completed: ${summary}.`);
      } else if (result.alreadyExists.length > 0 && result.failed.length === 0) {
        toast.error('No new users were created. All uploaded emails already exist.');
      } else {
        toast.error(`Bulk upload finished with issues: ${summary}.`);
      }
    } catch (error) {
      console.error('Error processing bulk upload:', error);
      toast.error(extractErrorMessage(error, 'Bulk upload failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Generate and download sample CSV file
  const downloadSampleFile = () => {
    const sampleData = [
      ['name', 'email', 'phone', 'role', 'password'],
      ['John Doe', 'john.doe@example.com', '+1 5551234567', 'customer', 'TempPass123!'],
      ['Jane Smith', 'jane.smith@example.com', '+1 5559876543', 'agent', 'TempPass123!'],
      ['Admin User', 'admin@example.com', '+1 5551112222', 'admin', 'TempPass123!']
    ];

    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'user_registration_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // All filtering is now handled server-side via API calls
  // No client-side filtering needed - registeredUsers already contains filtered results
  const filteredUsers = registeredUsers;

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'super-admin':
      case 'superadmin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'agent': return 'bg-green-100 text-green-800 border-green-200';
      case 'team-lead': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'customer': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'developer': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'suspended': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'User Name',
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold">
              {row.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <div className="font-semibold">{row.name || 'N/A'}</div>
            <div className="text-sm text-gray-500">{row.email || 'N/A'}</div>
          </div>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (value, row) => (
        <div className="text-sm text-gray-600">{row.phone || row.profile?.phone || 'N/A'}</div>
      )
    },
    {
      key: 'role',
      label: 'Role',
      render: (value, row) => {
        // Use roleName if available, otherwise use role code formatted nicely
        const displayRole = row.roleName || value || 'customer';
        // Format role for display: super-admin -> Super Admin, team-lead -> Team Lead
        const formattedRole = displayRole
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return (
          <Badge variant="outline" className={getRoleBadgeColor(value || 'customer')}>
            {formattedRole}
          </Badge>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant="outline" className={getStatusBadgeColor(value)}>
          {value || 'active'}
        </Badge>
      )
    },
    {
      key: 'isPasswordChanged',
      label: 'Pwd Changed',
      render: (value, row) => {
        const changed = resolvePasswordChangedFlag(row);
        return (
          <Badge
            variant="outline"
            className={
              changed
                ? 'bg-green-50 text-green-800 border-green-300'
                : 'bg-amber-50 text-amber-900 border-amber-300'
            }
          >
            {changed ? 'TRUE' : 'FALSE'}
          </Badge>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Registered',
      render: (value) => (
        <span className="text-sm text-gray-600">
          {value ? new Date(value).toLocaleDateString() : 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            title="Edit User"
            onClick={() => handleEditUser(row)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            title="Delete User"
            onClick={() => handleDeleteUser(row.id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 px-4 sm:px-6 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/background-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(2px)',
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-white/70"></div>

        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 border border-primary/20 rounded-full">
                <span className="text-xs sm:text-sm font-semibold text-primary">User Management</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground drop-shadow-lg">
                Register User
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground">
                Register new users (Admin, Agent, Team Lead, Customer) and manage user accounts
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="relative">
                <Button
                  variant="outline"
                  disabled={loadingUsers || exporting}
                  className="border-2 hover:bg-primary hover:text-primary-foreground w-full sm:w-auto"
                  onClick={() => setExportMenuOpen((prev) => !prev)}
                >
                  <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                  {exporting ? 'Exporting...' : 'Export'}
                  <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                </Button>
                {exportMenuOpen && !exporting && (
                  <>
                    <div className="fixed inset-0 z-10" aria-hidden onClick={() => setExportMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 min-w-[160px] rounded-md border border-border bg-card py-1 shadow-lg">
                      <button
                        type="button"
                        onClick={handleExportCsv}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Export CSV
                      </button>
                      <button
                        type="button"
                        onClick={handleExportPdf}
                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <FileText className="h-4 w-4" />
                        Export PDF
                      </button>
                    </div>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                className="border-2 hover:bg-primary hover:text-primary-foreground w-full sm:w-auto"
                onClick={() => setIsBulkUploadModalOpen(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Upload File</span>
                <span className="sm:hidden">Upload</span>
              </Button>
              <Button
                className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow w-full sm:w-auto"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="p-4 sm:p-6 relative">
        {/* Search and Filter */}
        <Card className="mb-4 sm:mb-6 gradient-card group hover:shadow-elegant transition-smooth border-2 hover:border-primary/30">
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  id="register-user-directory-query"
                  name="register-user-directory-query"
                  type="search"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  readOnly={!userListSearchUnlocked}
                  onFocus={() => setUserListSearchUnlocked(true)}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e?.target?.value || '');
                    setCurrentPage(0); // Reset to first page when searching
                  }}
                  className="pl-11 h-11 bg-card border-2 border-border focus-visible:ring-primary"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-muted-foreground" />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(0); // Reset to first page when filter changes
                  }}
                  className="flex-1 px-3 py-2 h-11 bg-card border-2 border-border rounded-md focus:ring-2 focus:ring-primary text-sm font-medium"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilterModal(!showFilterModal)}
                className="h-11 bg-card border-2 hover:bg-primary hover:text-primary-foreground"
              >
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="group hover:shadow-luxury transition-smooth border-2 hover:border-primary/30 overflow-hidden relative gradient-card">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardHeader className="relative z-10">
            <div className="flex items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                  <Users className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>{filteredUsers.length} users registered</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-muted-foreground">Loading users...</span>
              </div>
            ) : registeredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Users className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            ) : (
              <DataTable
                data={registeredUsers}
                columns={columns}
                pagination={false}
                searchable={false}
                sortable={true}
                itemsPerPage={pageSize}
                showItemsPerPageSelector={false}
              />
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing <strong className="text-foreground">{registeredUsers.length}</strong> of <strong className="text-foreground">{totalUsers}</strong> users
              {totalPages > 0 && (
                <span className="ml-2">(Page {currentPage + 1} of {totalPages})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0 || loadingUsers}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-muted-foreground px-2">
                {currentPage + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1 || loadingUsers}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
        {totalPages === 1 && totalUsers > 0 && (
          <div className="mt-6 text-sm text-muted-foreground">
            Showing <strong className="text-foreground">{registeredUsers.length}</strong> of <strong className="text-foreground">{totalUsers}</strong> users
            <span className="ml-2 text-xs">(All users displayed)</span>
          </div>
        )}

        {/* Create User Modal */}
        {isCreateModalOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[112]"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingUser(null);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  defaultPassword: '',
                  role: '',
                  department: '',
                  category: '',
                  isActive: false,
                  isPasswordChanged: false
                });
                // setCountryCode('+254');
                setErrors({});
              }}
            />
            <Dialog open={isCreateModalOpen} onOpenChange={(open) => {
              if (!open) {
                setIsCreateModalOpen(false);
                setEditingUser(null);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  defaultPassword: '',
                  role: '',
                  department: '',
                  category: '',
                  isActive: false,
                  isPasswordChanged: false
                });
                // setCountryCode('+254');
                setErrors({});
              } else {
                setIsCreateModalOpen(true);
              }
            }}>
              <DialogContent className="w-[95vw] sm:w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col p-4 sm:p-5 gap-0">
                <div className="gradient-primary p-1 flex flex-col min-h-0 max-h-full rounded-lg">
                  <div className="bg-card flex flex-col min-h-0 rounded-md">
                    {/* Fixed Header */}
                    <DialogHeader className="mb-2 flex-shrink-0 px-6 pt-6 pb-2">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                          <Plus className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <DialogTitle className="text-2xl font-bold">
                          {editingUser ? 'Edit User' : 'Create New User'}
                        </DialogTitle>
                      </div>
                    </DialogHeader>

                    {/* Content Area - scroll only when fields overflow */}
                    <div className="flex-1 min-h-0 overflow-y-auto px-6">
                      <div className="space-y-4 pb-2">
                        <div ref={(el) => { fieldScrollRefs.current.name = el; }}>
                          <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                          {/* <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e?.target?.value || '')}
                            placeholder="Enter full name"
                            className={errors.name ? 'border-red-500' : ''}
                          /> */}
                          <Input
                            id="name"
                            name="no_autofill_name"
                            type="text"
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck={false}
                            readOnly={!userListSearchUnlocked}
                            onFocus={() => setUserListSearchUnlocked(true)}
                            data-lpignore="true"
                            data-1p-ignore="true"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e?.target?.value || '')}
                            placeholder="Enter full name"
                            className={errors.name ? 'border-red-500' : ''}
                          />
                          {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                        </div>
                        <div ref={(el) => { fieldScrollRefs.current.email = el; }}>
                          <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                          <div className="relative">
                            <Input
                              id="email"
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e?.target?.value || '')}
                              onBlur={(e) => checkEmailAvailability(e.target.value)}
                              placeholder="Enter email address"
                              className={`${errors.email ? 'border-red-500' : ''} ${emailAvailable === true ? 'border-green-500' : ''} ${emailChecking ? 'pr-10' : emailAvailable !== null ? 'pr-10' : ''} ${editingUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              disabled={emailChecking || editingUser}
                            />
                            {emailChecking && (
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              </div>
                            )}
                            {!emailChecking && emailAvailable === true && (
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                            {!emailChecking && emailAvailable === false && (
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                <AlertCircle className="h-5 w-5 text-red-500" />
                              </div>
                            )}
                          </div>
                          {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
                          {emailAvailable === true && !errors.email && (
                            <p className="text-sm text-green-600 mt-1">Email is valid</p>
                          )}
                        </div>
                        <div ref={(el) => { fieldScrollRefs.current.phone = el; }}>
                          <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
                          <div className="flex gap-2">
                            <div className="w-32 relative">
                              <Select
                                value={phoneRegion}
                                onValueChange={(value) => {
                                  setPhoneRegion(value);
                                  setPhoneAvailable(null);
                                  if (formData.phone.trim()) {
                                    schedulePhoneValidation(formData.phone);
                                  }
                                }}
                              >
                                <SelectTrigger className="h-10 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="z-[60]" disableScrollbar={true}>
                                  {PHONE_COUNTRY_OPTIONS.map((code) => (
                                    <SelectItem key={code.region} value={code.region}>
                                      {code.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1 relative">
                              <Input
                                id="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e?.target?.value || '')}
                                onBlur={(e) => handlePhoneBlur(e.target.value)}
                                placeholder="Enter phone number (9–10 digits)"
                                className={`${errors.phone ? 'border-red-500' : ''} ${phoneAvailable === true ? 'border-green-500' : ''} ${phoneChecking || phoneAvailable !== null ? 'pr-10' : ''}`}
                                disabled={phoneChecking}
                              />
                              {phoneChecking && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                                </div>
                              )}
                              {!phoneChecking && phoneAvailable === true && !errors.phone && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                              {!phoneChecking && (phoneAvailable === false || errors.phone) && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                  <AlertCircle className="h-5 w-5 text-red-500" />
                                </div>
                              )}
                            </div>
                          </div>
                          {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
                          {phoneAvailable === true && !errors.phone && (
                            <p className="text-sm text-green-600 mt-1">Phone number is available</p>
                          )}
                        </div>
                        <div ref={(el) => { fieldScrollRefs.current.defaultPassword = el; }}>
                          <Label htmlFor="defaultPassword">
                            Default Password {!editingUser && <span className="text-red-500">*</span>}
                            {editingUser && <span className="text-gray-500 text-xs ml-2">(Leave blank to keep current password)</span>}
                          </Label>
                          <DefaultPasswordRequirementsPopover password={formData.defaultPassword}>
                            <div className="relative">
                              <Input
                                id="defaultPassword"
                                name="new_user_pwd_field"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="off"
                                autoCapitalize="off"
                                autoCorrect="off"
                                spellCheck={false}
                                readOnly={!passwordFieldUnlocked}
                                onFocus={() => setPasswordFieldUnlocked(true)}
                                data-lpignore="true"
                                data-1p-ignore="true"
                                value={formData.defaultPassword}
                                onChange={(e) => handleInputChange('defaultPassword', e?.target?.value || '')}
                                placeholder="Enter default password (min 8 characters)"
                                className={defaultPasswordInputInvalid ? 'border-red-500 pr-10' : 'pr-10'}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                              >
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                              </button>
                            </div>
                          </DefaultPasswordRequirementsPopover>
                          {errors.defaultPassword && <p className="text-sm text-red-600 mt-1">{errors.defaultPassword}</p>}
                        </div>
                        <div className="relative" ref={(el) => { fieldScrollRefs.current.role = el; }}>
                          <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
                          <Select
                            value={formData.role}
                            onValueChange={(value) => handleInputChange('role', value)}
                            disabled={loadingRoles}
                          >
                            <SelectTrigger className={errors.role ? 'border-red-500 h-10' : 'h-10'}>
                              <SelectValue placeholder={loadingRoles ? "Loading roles..." : "Select a role"} />
                            </SelectTrigger>
                            <SelectContent className="z-[60]">
                              {loadingRoles ? (
                                <SelectItem value="" disabled>Loading roles...</SelectItem>
                              ) : roles.length === 0 ? (
                                <SelectItem value="" disabled>No roles available</SelectItem>
                              ) : (
                                roles.map((role) => (
                                  <SelectItem key={role.value} value={role.value}>
                                    <div className="flex items-center gap-2">
                                      <Shield className="h-4 w-4 text-gray-500" />
                                      {role.label}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {errors.role && <p className="text-sm text-red-600 mt-1">{errors.role}</p>}
                        </div>
                        {/* Department Dropdown - Optional for all roles */}
                        {/* COMMENTED OUT: Department field hidden per requirement */}
                        {/* {formData.role && formData.role !== 'customer' && (
                          <div className="relative">
                            <Label htmlFor="department">
                              Department
                            </Label>
                            <Select
                              value={formData.department}
                              onValueChange={(value) => handleInputChange('department', value)}
                            >
                              <SelectTrigger className={errors.department ? 'border-red-500 h-10' : 'h-10'}>
                                <SelectValue placeholder="Select department" />
                              </SelectTrigger>
                              <SelectContent className="z-[60]">
                                {loadingDepartments ? (
                                  <SelectItem value="" disabled>Loading departments...</SelectItem>
                                ) : departments.length === 0 ? (
                                  <SelectItem value="" disabled>No departments available</SelectItem>
                                ) : (
                                  departments.map((dept) => {
                                    // Backend returns: departmentId, departmentName, departmentCode
                                    // Support both formats for compatibility
                                    const deptId = dept.departmentId || dept.id;
                                    const deptName = dept.departmentName || dept.name;
                                    const deptCode = dept.departmentCode || dept.code;
                                    
                                    // Ensure deptId is valid before rendering
                                    if (!deptId) {
                                      console.warn('Department missing ID:', dept);
                                      return null;
                                    }
                                    
                                    return (
                                      <SelectItem key={deptId} value={deptId.toString()}>
                                        {deptName} {deptCode ? `(${deptCode})` : ''}
                                      </SelectItem>
                                    );
                                  }).filter(Boolean) // Remove null entries
                                )}
                              </SelectContent>
                            </Select>
                            {errors.department && <p className="text-sm text-red-600 mt-1">{errors.department}</p>}
                          </div>
                        )} */}
                        {/* Category Dropdown - Only for Agent role */}
                        {formData.role === 'agent' && (
                          <div className="relative">
                            <Label htmlFor="category">
                              Category
                            </Label>
                            <Select
                              value={formData.category}
                              onValueChange={(value) => handleInputChange('category', value)}
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue placeholder="Select category (optional)" />
                              </SelectTrigger>
                              <SelectContent className="z-[60]">
                                {loadingCategories ? (
                                  <SelectItem value="" disabled>Loading categories...</SelectItem>
                                ) : categories.length === 0 ? (
                                  <SelectItem value="" disabled>No categories available</SelectItem>
                                ) : (
                                  categories.map((cat) => {
                                    // Backend returns: categoryId, categoryName
                                    const catId = cat.categoryId || cat.id;
                                    const catName = cat.categoryName || cat.name;

                                    if (!catId) {
                                      console.warn('Category missing ID:', cat);
                                      return null;
                                    }

                                    return (
                                      <SelectItem key={catId} value={catId.toString()}>
                                        {catName}
                                      </SelectItem>
                                    );
                                  }).filter(Boolean)
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {/* Account status + password-changed flag (read-only) — edit only */}
                        {editingUser && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="relative">
                              <Label htmlFor="accountStatus">
                                Account Status <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={formData.isActive ? 'active' : 'inactive'}
                                onValueChange={(value) => handleInputChange('isActive', value === 'active')}
                              >
                                <SelectTrigger className="h-10">
                                  <SelectValue placeholder="Select account status" />
                                </SelectTrigger>
                                <SelectContent className="z-[60]">
                                  <SelectItem value="active">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                      Active - User can login normally
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="inactive">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                      Inactive - User cannot log in
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-gray-500 mt-1">
                                {formData.isActive
                                  ? 'User can sign in with their password after you save changes'
                                  : 'User cannot sign in until the account is set to Active'}
                              </p>
                            </div>
                            <div className="relative">
                              <Label>Password Changed Status</Label>
                              <div
                                className={`h-10 flex items-center gap-2 px-3 rounded-md border font-medium text-sm ${
                                  formData.isPasswordChanged
                                    ? 'bg-green-50 border-green-500 text-green-800'
                                    : 'bg-amber-50 border-amber-500 text-amber-900'
                                }`}
                                aria-live="polite"
                              >
                                <span
                                  className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                                    formData.isPasswordChanged ? 'bg-green-600' : 'bg-amber-600'
                                  }`}
                                />
                                {formData.isPasswordChanged ? 'TRUE' : 'FALSE'}
                                <span className="text-xs font-normal opacity-80">
                                  (is_password_changed)
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {formData.isPasswordChanged
                                  ? 'User can use normal password login.'
                                  : 'Legacy users may still have FALSE until you save an update — then it becomes TRUE automatically.'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fixed Footer */}
                    <div className="flex justify-end space-x-2 pt-4 border-t px-6 pb-6 flex-shrink-0 mt-2">
                      <Button variant="outline" onClick={() => {
                        setIsCreateModalOpen(false);
                        setEditingUser(null);
                        setFormData({
                          name: '',
                          email: '',
                          phone: '',
                          defaultPassword: '',
                          role: '',
                          department: '',
                          category: ''
                        });
                        // setCountryCode('+254');
                        setErrors({});
                      }}>
                        Cancel
                      </Button>
                      <Button
                        className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow"
                        onClick={handleCreateUser}
                        disabled={loading || createUserPasswordBlocked}
                      >
                        {loading ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
                      </Button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCreateModalOpen(false);
                    setEditingUser(null);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      defaultPassword: '',
                      role: '',
                      department: '',
                      category: ''
                    });
                    // setCountryCode('+254');
                    setErrors({});
                  }}
                  className="absolute right-4 top-4 z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background"
                  type="button"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Bulk Upload Modal - Same as super-admin */}
        {isBulkUploadModalOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[112]"
              onClick={() => {
                if (!uploadingFile && !loading) {
                  setIsBulkUploadModalOpen(false);
                  resetBulkUploadState();
                }
              }}
            />
            <Dialog open={isBulkUploadModalOpen} onOpenChange={(open) => {
              if (!open && !uploadingFile && !loading) {
                setIsBulkUploadModalOpen(false);
                resetBulkUploadState();
              }
            }}>
              <DialogContent className="w-[95vw] sm:w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="gradient-primary p-1 flex flex-col h-full overflow-hidden rounded-lg">
                  <div className="bg-card flex flex-col h-full overflow-hidden rounded-md p-5 sm:p-6 gap-4">
                    {/* Fixed Header */}
                    <DialogHeader className="flex-shrink-0 p-0 m-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-accent shadow-glow">
                          <Upload className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <DialogTitle className="text-2xl font-bold">Bulk User Registration</DialogTitle>
                      </div>
                    </DialogHeader>

                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 -mx-1 px-1">
                      <div className="space-y-4 pb-1">
                        {/* File Format Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-blue-900 mb-2">File Format Requirements</h4>
                              <div className="text-xs text-blue-800 space-y-1">
                                <p><strong>Supported Formats:</strong> CSV (.csv) or Excel (.xlsx, .xls)</p>
                                <p><strong>Note:</strong> PDF files are not fully supported. Please use CSV or Excel format.</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Expected Columns Info */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-amber-900 mb-2">Expected Columns</h4>
                              <div className="text-xs text-amber-800 space-y-1">
                                <p><strong>Required Columns:</strong></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><strong>name</strong> (or "full name", "fullname") - User's full name</li>
                                  <li><strong>email</strong> (or "email address") - Valid email address</li>
                                </ul>
                                <p className="mt-2"><strong>Optional Columns:</strong></p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                  <li><strong>phone</strong> (or "phone number", "mobile") - Phone number with country code</li>
                                  <li><strong>role</strong> - User role (admin, agent, team-lead, customer). Default: customer</li>
                                  <li><strong>password</strong> (or "default password") - Minimum 8 characters. Default: Auto-generated</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Sample File Download */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <FileText className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-green-900 mb-1">Download Sample Template</h4>
                                <p className="text-xs text-green-800">
                                  Download a sample CSV file with the correct format. Edit it with your user data and upload.
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={downloadSampleFile}
                              className="ml-4 border-green-300 text-green-700 hover:bg-green-100"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download Template
                            </Button>
                          </div>
                        </div>

                        {parsedUsers.length === 0 ? (
                          <>
                            <div>
                              <Label>Upload File</Label>
                              <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
                                <input
                                  ref={bulkFileInputRef}
                                  type="file"
                                  accept=".csv,.xlsx,.xls"
                                  onChange={handleFileSelect}
                                  className="hidden"
                                  id="bulk-file-upload"
                                  disabled={uploadingFile}
                                />
                                <label htmlFor="bulk-file-upload" className="cursor-pointer">
                                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                  <p className="text-sm font-medium text-gray-700 mb-2">
                                    {uploadingFile ? 'Processing file...' : 'Click to upload or drag and drop'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    CSV or Excel (.xlsx, .xls) files only
                                  </p>
                                </label>
                              </div>
                              {selectedFile && (
                                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                                  <FileText className="h-4 w-4" />
                                  <span>{selectedFile.name}</span>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <p className="text-sm font-medium text-green-800">
                                File processed successfully! Found {parsedUsers.length} user(s) ready to register.
                              </p>
                            </div>
                            {bulkUploadResult && (
                              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <p className="text-sm font-semibold text-gray-900">Upload result</p>
                                {bulkUploadResult.created.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-green-700 mb-1">
                                      {bulkUploadResult.created.length} user(s) registered successfully:
                                    </p>
                                    <ul className="max-h-24 overflow-y-auto text-xs text-green-800 list-disc list-inside space-y-0.5">
                                      {bulkUploadResult.created.map((u) => (
                                        <li key={u.email}>
                                          {u.name ? <>{u.name} - {u.email}</> : u.email}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {bulkUploadResult.alreadyExists.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-amber-800 mb-1">
                                      {bulkUploadResult.alreadyExists.length} user(s) already exist (not created):
                                    </p>
                                    <ul className="max-h-24 overflow-y-auto text-xs text-amber-900 list-disc list-inside space-y-0.5">
                                      {bulkUploadResult.alreadyExists.map((u) => (
                                        <li key={u.email}>
                                          {u.email}
                                          {u.reason ? <> - {u.reason}</> : null}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {bulkUploadResult.failed.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-red-700 mb-1">
                                      {bulkUploadResult.failed.length} user(s) failed:
                                    </p>
                                    <ul className="max-h-24 overflow-y-auto text-xs text-red-800 list-disc list-inside space-y-0.5">
                                      {bulkUploadResult.failed.map((u) => (
                                        <li key={u.email}>
                                          {u.email} - {u.reason}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <div className="overflow-x-auto max-h-64 overflow-y-auto -mx-1">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                      <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Name</th>
                                      <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Email</th>
                                      <th className="px-4 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Role</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {parsedUsers.map((user, index) => (
                                      <tr key={index}>
                                        <td className="px-4 py-2 whitespace-nowrap">{user.name}</td>
                                        <td className="px-4 py-2 whitespace-nowrap text-xs">{user.email}</td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                          <Badge variant="outline" className="text-xs">{user.role || 'customer'}</Badge>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Fixed Footer */}
                    {parsedUsers.length > 0 && (
                      <div className="flex justify-end space-x-2 pt-4 border-t flex-shrink-0">
                        <Button
                          variant="outline"
                          onClick={resetBulkUploadState}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="gradient-primary text-primary-foreground shadow-elegant hover:shadow-glow"
                          onClick={handleBulkCreateUsers}
                          disabled={loading}
                        >
                          {loading ? 'Creating...' : `Create ${parsedUsers.length} Users`}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!uploadingFile && parsedUsers.length === 0) {
                      setIsBulkUploadModalOpen(false);
                    }
                  }}
                  className="absolute right-4 top-4 z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background"
                  type="button"
                  disabled={uploadingFile}
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogContent>
            </Dialog>
          </>
        )}

        {/* Filter Modal */}
        {showFilterModal && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[112]"
              onClick={() => setShowFilterModal(false)}
            />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[125] bg-white rounded-lg shadow-lg p-6 w-96">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Advanced Filters</h3>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Role</Label>
                  <select
                    value={roleFilter}
                    onChange={(e) => {
                      setRoleFilter(e.target.value);
                      setCurrentPage(0); // Reset to first page when filter changes
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md mt-1"
                  >
                    <option value="all">All Roles</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setRoleFilter('all');
                    setStatusFilter('all');
                    setSearchTerm('');
                    setCurrentPage(0); // Reset page when clearing filters
                    setShowFilterModal(false);
                  }}>
                    Clear All Filters
                  </Button>
                  <Button onClick={() => setShowFilterModal(false)}>
                    Apply Filters
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDeleteUser}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </div>
  );
};

export default Register;

